import { useAtom } from 'jotai';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { userAtom } from '../jotai-atoms/user.atom';

interface ReservationDashboardProps {}

export const ReservationDashboard: React.FC<ReservationDashboardProps> = () => {
  const __ = useTranslation().t;

  const [user] = useAtom(userAtom);

  const renewDate = user?.subscriptionInfo?.renewDate;
  const numberOfGroupClassesTotal = user?.subscriptionInfo?.groupSubscriptionTotal || 0;
  const numberOfGroupClassesTaken = user?.subscriptionInfo?.groupSubscriptionTaken || 0;
  const numberOfGroupClassesAllowed =
    (user?.subscriptionInfo?.groupSubscriptionTotal || 0) +
    (user?.subscriptionInfo?.groupSubscriptionCredited || 0);
  const numberOfPrivateClassesTaken = user?.subscriptionInfo?.privateSubscriptionTaken || 0;
  const numberOfPrivateClassesAllowed =
    (user?.subscriptionInfo?.privateSubscriptionTotal || 0) +
    (user?.subscriptionInfo?.privateSubscriptionCredited || 0);
  const hasSubscriptionTypeUnlimitedGroup =
    user?.subscriptionInfo?.hasSubscriptionTypeUnlimitedGroup;
  const hasPrivateClassesAllowed = numberOfPrivateClassesAllowed > 0;

  return (
    <Container>
      <StyledColumn firstChild>
        <StyledLabel>{__('mySubscription')}</StyledLabel>
        <StyledValue>
          {hasSubscriptionTypeUnlimitedGroup ? (
            <>{__('unlimited')}</>
          ) : (
            <>
              <span>{__('numberOfClasses', { 1: numberOfGroupClassesTotal })}</span>
              <StyledValueDescription>{__('perMonth')}</StyledValueDescription>
            </>
          )}
        </StyledValue>
      </StyledColumn>
      <StyledColumn hide={hasSubscriptionTypeUnlimitedGroup}>
        <StyledLabel>{__('taken')}</StyledLabel>
        <StyledValue>
          {__('currentProgressOutOfTotal', {
            1: numberOfGroupClassesTaken,
            2: numberOfGroupClassesAllowed,
          })}
        </StyledValue>
      </StyledColumn>
      <StyledColumn hide={hasSubscriptionTypeUnlimitedGroup}>
        <StyledLabel>{__('classesRenewed')}</StyledLabel>
        <StyledValue>{renewDate || '-'}</StyledValue>
      </StyledColumn>
      <StyledColumn hide={!hasPrivateClassesAllowed}>
        <StyledLabel>{__('privateClasses')}</StyledLabel>
        <StyledValue>
          {__('currentProgressOutOfTotal', {
            1: numberOfPrivateClassesTaken,
            2: numberOfPrivateClassesAllowed,
          })}
        </StyledValue>
      </StyledColumn>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: space-around;
  color: ${({ theme }) => theme.colors.dark};
`;

const StyledLabel = styled.div`
  font-family: 'Inter';
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  margin-bottom: 4px;
`;

const StyledValue = styled.div`
  font-family: 'Baloo 2';
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
`;

const StyledValueDescription = styled.div`
  font-family: 'Baloo 2';
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
`;

interface StyledColumnProps {
  firstChild?: boolean;
  hide?: boolean;
}

const StyledColumn = styled.div<StyledColumnProps>`
  ${({ hide }) => (hide ? `display: none;` : ``)}
  flex: 1;
  padding: 4px;
  text-align: center;
  ${({ firstChild, theme }) =>
    firstChild ? `` : `border-left: 1px solid ${theme.colors.backgroundDark};`}
`;
